// pages/inventory/SCRetrededTyreIssueEntry.jsx
// ✅ Converted from: SCRetrededTyreIssueEntry.aspx + SCRetrededTyreIssueEntry.aspx.cs
// ✅ ALL business logic preserved (search, add, remove, serial no, submit with transaction)
// ✅ NO rights logic (excluded as instructed)
// ✅ Pagination added to Grid 1 (Item Search Results)
// ✅ Financial year date validation on Date & Due Date fields
// ✅ Autocomplete on Item Code & Item Name

import { useEffect, useContext, useState, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft, Search, Plus, Trash2, CheckSquare, ClipboardList,
  RefreshCcw, ChevronLeft, ChevronRight, Link2
} from "lucide-react";

const BASE_URL = "http://nissi-india.com";

// ─────────────── helpers ───────────────
const s = (v) => (v ?? "").toString().trim();
const toInt = (v, def = 0) => { const n = Number(v); return Number.isFinite(n) ? n : def; };

const today00 = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };

// dd/MM/yyyy → Date
const dmyToDate = (dmy) => {
  const x = s(dmy); if (!x) return null;
  const m = x.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
  return isNaN(d.getTime()) ? null : d;
};

// ISO → Date
const isoToDate = (iso) => {
  const x = s(iso); if (!x) return null;
  const d = new Date(`${x}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

// ISO → dd/MM/yyyy
const isoToDmy = (iso) => {
  const d = isoToDate(iso); if (!d) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// dd/MM/yyyy → ISO
const dmyToIso = (dmy) => {
  const d = dmyToDate(dmy); if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// today ISO
const todayIso = () => new Date().toISOString().slice(0, 10);

// ─────────────── component ───────────────
const SCRetrededTyreIssueEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // session values
  const branchId   = localStorage.getItem("branchId") || "";
  const finYearId  = localStorage.getItem("finYearId") || "";
  const loginUserId = localStorage.getItem("userId") || "";
  const financeYearStartDate = localStorage.getItem("financeYearStartDate") || ""; // dd/MM/yyyy
  const financeYearEndDate   = localStorage.getItem("financeYearEndDate") || "";   // dd/MM/yyyy

  // ── header fields ──
  const [date, setDate]           = useState(""); // ISO
  const [dueDate, setDueDate]     = useState(""); // ISO
  const [branches, setBranches]   = useState([]);
  const [selBranch, setSelBranch] = useState("Select");
  const [suppliers, setSuppliers] = useState([]);
  const [selSupplier, setSelSupplier] = useState("Select");

  // ── filter area ──
  const [categories, setCategories]   = useState([]);
  const [selCategory, setSelCategory] = useState("Select");
  const [itemCode, setItemCode]       = useState("");
  const [itemName, setItemName]       = useState("");

  // ── autocomplete ── 
  const [acCodeItems, setAcCodeItems] = useState([]);
  const [acCodeOpen, setAcCodeOpen]   = useState(false);
  const [acNameItems, setAcNameItems] = useState([]);
  const [acNameOpen, setAcNameOpen]   = useState(false);

  // ── Grid 1: search results ──
  const [searchRows, setSearchRows]   = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage]   = useState(1);
  const [searchPageSize]              = useState(10);

  // checked rows in Grid 1
  const [grid1Checked, setGrid1Checked] = useState({});

  // ── Grid 2: added items (cart) ──
  const [cartRows, setCartRows]         = useState([]);
  const [cartChecked, setCartChecked]   = useState({});
  const [cartRemoveChecked, setCartRemoveChecked] = useState({});
  const [submitting, setSubmitting]     = useState(false);

  // ─── derived ───
  const searchTotal      = searchRows.length;
  const searchTotalPages = Math.max(1, Math.ceil(searchTotal / searchPageSize));
  const pagedSearchRows  = useMemo(() => {
    const start = (searchPage - 1) * searchPageSize;
    return searchRows.slice(start, start + searchPageSize);
  }, [searchRows, searchPage, searchPageSize]);

  // ─── styles ───
  const inputBase =
    `w-full px-4 py-2.5 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/50 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");

  const selectBase =
    `w-full px-4 py-2.5 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/50 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-400");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold shadow ` +
    `hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-700 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold shadow ` +
    `bg-red-600 hover:bg-red-700 transition-all duration-200`;

  const thBase = `px-3 py-3 text-xs font-bold uppercase tracking-wider text-white text-center`;
  const tdBase = `px-3 py-3 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-800"}`;

  // ─── date validation (matches ASP checkDate) ───
  const validateDateField = (isoVal, label) => {
    if (!isoVal) return true; // blank handled separately
    const d  = isoToDate(isoVal);
    const t  = today00();
    if (d > t) {
      alert(`${label}: You can select only Previous days`);
      return false;
    }
    const fyStart = dmyToDate(financeYearStartDate);
    const fyEnd   = dmyToDate(financeYearEndDate);
    if (fyStart && d < fyStart) {
      alert(`${label}: You can select only Greater than or Equal to Financial Year Date ${financeYearStartDate}`);
      return false;
    }
    if (fyEnd && d > fyEnd) {
      alert(`${label}: You can select only Less than or Equal to Financial Year End Date ${financeYearEndDate}`);
      return false;
    }
    return true;
  };

  // ─── load dropdowns on mount ───
  useEffect(() => {
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDropdowns = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/sc-retreded-tyre-issue/dropdowns`, {
        params: { branchId }
      });
      if (res.data?.success) {
        setBranches(res.data.branches || []);
        setSuppliers(res.data.suppliers || []);
        setCategories(res.data.categories || []);
      }
    } catch (err) {
      toast.error("Failed to load dropdowns");
    }
  };

  // ─── autocomplete fetcher (Item Code / Name) ───
  const fetchAc = useCallback(async (type, prefix) => {
    if (!prefix) {
      type === "Code" ? setAcCodeItems([]) : setAcNameItems([]);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/sc-retreded-tyre-issue/autocomplete`, {
        params: {
          type,         // 'Code' or 'Name'
          branchId,
          itemCode: type === "Code" ? prefix : itemCode,
          itemName: type === "Name" ? prefix : itemName,
          prefix
        }
      });
      if (res.data?.success) {
        const list = res.data.suggestions || [];
        type === "Code" ? setAcCodeItems(list) : setAcNameItems(list);
      }
    } catch { /* silent */ }
  }, [branchId, itemCode, itemName]);

  // ─── Search (Grid 1) ───
  const handleSearch = async () => {
    if (selBranch === "Select") {
      alert("Please Select Store"); return;
    }
    try {
      setSearchLoading(true);
      setSearchPage(1);
      const res = await axios.get(`${BASE_URL}/api/inventory/sc-retreded-tyre-issue/search-items`, {
        params: {
          branchId,
          selBranch,
          itemCategory: selCategory,
          itemCode: itemCode ? `${itemCode}%` : "%",
          itemName: itemName ? `%${itemName}%` : "%"
        }
      });
      if (res.data?.success) {
        const rows = res.data.items || [];
        if (rows.length === 0) {
          // ASP: show specific alert based on HO branch check
          const msg = res.data.message || "No items found";
          alert(msg);
        }
        setSearchRows(rows);
        setGrid1Checked({});
      } else {
        alert(res.data?.message || "Search failed");
        setSearchRows([]);
      }
    } catch (err) {
      toast.error("Search failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setSearchLoading(false);
    }
  };

  // ─── Add checked items from Grid 1 → Grid 2 ───
  const handleAddItems = async () => {
    const checkedIds = Object.entries(grid1Checked)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (checkedIds.length === 0) {
      alert("Please select at least one item");
      return;
    }

    const selectedItems = searchRows.filter(r => checkedIds.includes(String(r.itemid)));

    // fetch available tyre counts + serial no flag for each selected item
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/sc-retreded-tyre-issue/item-details`, {
        items: selectedItems.map(r => ({ itemid: r.itemid, itemname: r.itemname })),
        branchId: selBranch
      });

      console.log("data", res.data);
      

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load item details");
        return;
      }

      const detailMap = {};
      (res.data.details || []).forEach(d => { detailMap[String(d.itemid)] = d; });

      const newCart = [...cartRows];
      selectedItems.forEach(item => {
        const detail = detailMap[String(item.itemid)] || {};
        const tyreCount = toInt(detail.tyreCount);
        if (tyreCount <= 0) return; // ASP: only add if count > 0

        // skip duplicates (ASP: check itemid + fld_qty > 0)
        const exists = newCart.some(c => String(c.itemid) === String(item.itemid) && toInt(c.fld_qty) > 0);
        if (exists) return;

        newCart.push({
          itemid:        item.itemid,
          itemcode:      item.itemcode,
          itemname:      item.itemname,
          itemPartNo:    item.itemPartNo || item.itempartno || "",
          itemuom:       item.itemuom,
          fld_qty:       String(tyreCount),
          fld_IsSerialNo: detail.fld_IsSerialNo === "True" || detail.fld_IsSerialNo === true,
          serialno:      detail.fld_IsSerialNo === "True" || detail.fld_IsSerialNo === true ? "Yes" : "No",
          AvailableStock: item.fld_closing_stock || 0
        });
      });

      setCartRows(newCart);
      setSearchRows([]);     // ASP: gv_itemmaster.Visible = false after add
      setGrid1Checked({});
    } catch (err) {
      toast.error("Failed to add items: " + (err?.response?.data?.message || err.message));
    }
  };

  // ─── Remove checked rows from Grid 2 ───
  const handleRemoveItems = () => {
    const toRemove = Object.entries(cartRemoveChecked)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (toRemove.length === 0) {
      alert("Please select items to remove");
      return;
    }
    if (!window.confirm("Remove selected items?")) return;
    setCartRows(prev => prev.filter(r => !toRemove.includes(String(r.itemid))));
    setCartRemoveChecked({});
  };

  // ─── Open Serial No popup ───
  const handleSerialNo = (row) => {
    const qty = s(getCartQty(row.itemid));
    if (!qty) {
      alert("Please enter Transfer qty and try again"); return;
    }
    if (qty.includes(".")) {
      alert("Serial no entry not allowed for decimal transfer quantity"); return;
    }
    if (toInt(qty) <= 0) {
      alert("Serial no entry not allowed for Transfer quantity 0"); return;
    }
    if (!date) {
      alert("Please select Date first"); return;
    }
    const isoDate = date.replace(/-/g, "/"); // yyyy/MM/dd format for SP
    const url = `SerialNoEntry?item=${row.itemid}-${selBranch}-${branchId}-${isoDate}&qty=${qty}&pro=SCRI&f=1`;
    window.open(url, "_blank", "width=800,height=800,scrollbars=yes,menubar=no,resizable=no");
  };

  // ─── Cart qty helpers ───
  const getCartQty = (itemid) => {
    const row = cartRows.find(r => String(r.itemid) === String(itemid));
    return row ? row.fld_qty : "";
  };

  const setCartQty = (itemid, val) => {
    setCartRows(prev =>
      prev.map(r => String(r.itemid) === String(itemid) ? { ...r, fld_qty: val } : r)
    );
  };

  // ─── Submit ───
  const handleSubmit = async () => {
    // Validation
    if (!date)          { alert("Pls Enter Date"); return; }
    if (!dueDate)       { alert("Pls Enter Due Date"); return; }
    if (selBranch === "Select")   { alert("Pls Select Branch"); return; }
    if (selSupplier === "Select") { alert("Pls Select Supplier"); return; }

    const checkedCart = cartRows.filter(r => cartChecked[String(r.itemid)]);
    if (checkedCart.length === 0) {
      alert("Please select at least one item to submit"); return;
    }

    // Serial no validation
    for (const row of checkedCart) {
      const qty = toInt(getCartQty(row.itemid));
      if (row.fld_IsSerialNo) {
        // check session storage for serial nos
        const stored = sessionStorage.getItem(`SCRBranchItemSno_${loginUserId}_${row.itemid}`);
        if (!stored) {
          alert(`Please select serial no for ${row.itemcode}`); return;
        }
        const snos = JSON.parse(stored);
        if (snos.length !== qty) {
          alert(`Transfer Qty and Serial No Miss-matches for ${row.itemcode}`); return;
        }
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        date: isoToDmy(date),
        dueDate: isoToDmy(dueDate),
        branchId: selBranch,
        supplierId: selSupplier,
        loginUserId,
        sessionBranchId: branchId,
        items: checkedCart.map(row => {
          const qty = getCartQty(row.itemid);
          const stored = sessionStorage.getItem(`SCRBranchItemSno_${loginUserId}_${row.itemid}`);
          const serialNos = stored ? JSON.parse(stored) : [];
          return {
            itemid: row.itemid,
            qty,
            isSerial: row.fld_IsSerialNo,
            serialNos
          };
        })
      };

      const res = await axios.post(`${BASE_URL}/api/inventory/sc-retreded-tyre-issue/submit`, payload);

      if (res.data?.success) {
        // clear serial no sessions
        checkedCart.forEach(row => {
          sessionStorage.removeItem(`SCRBranchItemSno_${loginUserId}_${row.itemid}`);
        });
        toast.success("Entry submitted successfully!");
        // ASP: Response.Redirect("SCRetrededTyreIssueEntry.aspx") -> reset page
        setTimeout(() => {
          setDate(""); setDueDate("");
          setSelBranch("Select"); setSelSupplier("Select");
          setCartRows([]); setSearchRows([]);
          setCartChecked({}); setCartRemoveChecked({});
        }, 1500);
      } else {
        alert(res.data?.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      alert("Submission failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Select All helpers ───
  const toggleAllGrid1 = (checked) => {
    const map = {};
    pagedSearchRows.forEach(r => { map[String(r.itemid)] = checked; });
    setGrid1Checked(map);
  };

  const toggleAllCart = (checked) => {
    const map = {};
    cartRows.forEach(r => { map[String(r.itemid)] = checked; });
    setCartChecked(map);
  };

  const toggleAllRemove = (checked) => {
    const map = {};
    cartRows.forEach(r => { map[String(r.itemid)] = checked; });
    setCartRemoveChecked(map);
  };

  // ─── Pagination buttons ───
  const paginationButtons = useMemo(() => {
    const win = 5;
    let start = Math.max(1, searchPage - Math.floor(win / 2));
    let end = Math.min(searchTotalPages, start + win - 1);
    if (end - start < win - 1) start = Math.max(1, end - win + 1);
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [searchPage, searchTotalPages]);

  const showingFrom = searchTotal === 0 ? 0 : (searchPage - 1) * searchPageSize + 1;
  const showingTo   = Math.min(searchPage * searchPageSize, searchTotal);

  // ────────────────────────────── RENDER ──────────────────────────────
  return (
    <LayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUB-CONTRACT RETREDED TYRE ISSUE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `${currentSkin.glass} bg-white text-black border-gray-200`
        }`}>

          {/* ── Page Header ── */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <ClipboardList className={isDark ? "text-white" : "text-gray-700"} />
              </div>
              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Sub-Contract Retreded Tyre Issued Entry
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search Items → Add to Cart → Submit
                </div>
              </div>
            </div>
            <button
              onClick={() => window.location.href = "/inventory/SCRetrededTyreIssueListing"}
              className={ghostBtn}
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>

          {/* ── Header Form ── */}
          <div className={`${cardBase} p-6 mb-6`}>
            <div className={`text-sm font-bold uppercase tracking-widest mb-4 ${isDark ? "text-gray-300" : "text-gray-500"}`}>
              Entry Details
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Date */}
              <div>
                <label className={labelBase}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  max={todayIso()}
                  onChange={e => setDate(e.target.value)}
                  onBlur={() => validateDateField(date, "Date")}
                  className={inputBase + " mt-1.5"}
                />
              </div>
              {/* Due Date */}
              <div>
                <label className={labelBase}>Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  onBlur={() => validateDateField(dueDate, "Due Date")}
                  className={inputBase + " mt-1.5"}
                />
              </div>
              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <select
                  value={selBranch}
                  onChange={e => setSelBranch(e.target.value)}
                  className={selectBase + " mt-1.5"}
                >
                  <option value="Select">Select</option>
                  {branches.map(b => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>
              {/* Supplier */}
              <div>
                <label className={labelBase}>Supplier</label>
                <select
                  value={selSupplier}
                  onChange={e => setSelSupplier(e.target.value)}
                  className={selectBase + " mt-1.5"}
                >
                  <option value="Select">Select</option>
                  {suppliers.map(sup => (
                    <option key={sup.value} value={sup.value}>{sup.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ── Filtration Area ── */}
          <div className={`${cardBase} p-6 mb-6`}>
            <div className={`text-sm font-bold uppercase tracking-widest mb-4 ${isDark ? "text-gray-300" : "text-gray-500"}`}>
              Filtration Area
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              {/* Item Category */}
              <div>
                <label className={labelBase}>Item Category</label>
                <select
                  value={selCategory}
                  onChange={e => setSelCategory(e.target.value)}
                  className={selectBase + " mt-1.5"}
                >
                  <option value="Select">Select</option>
                  {categories.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              {/* Item Code autocomplete */}
              <div className="relative">
                <label className={labelBase}>Item Code</label>
                <input
                  value={itemCode}
                  autoComplete="off"
                  placeholder="Type item code..."
                  onChange={e => {
                    setItemCode(e.target.value);
                    fetchAc("Code", e.target.value);
                    setAcCodeOpen(true);
                  }}
                  onBlur={() => setTimeout(() => setAcCodeOpen(false), 150)}
                  className={inputBase + " mt-1.5"}
                />
                {acCodeOpen && acCodeItems.length > 0 && (
                  <div className={`absolute z-50 mt-1 w-full rounded-xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {acCodeItems.map((x, i) => (
                      <button key={i} type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setItemCode(x); setAcCodeOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm ${isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"}`}
                      >{x}</button>
                    ))}
                  </div>
                )}
              </div>
              {/* Item Name autocomplete */}
              <div className="relative md:col-start-2">
                <label className={labelBase}>Item Name</label>
                <input
                  value={itemName}
                  autoComplete="off"
                  placeholder="Type item name..."
                  onChange={e => {
                    setItemName(e.target.value);
                    fetchAc("Name", e.target.value);
                    setAcNameOpen(true);
                  }}
                  onBlur={() => setTimeout(() => setAcNameOpen(false), 150)}
                  className={inputBase + " mt-1.5"}
                />
                {acNameOpen && acNameItems.length > 0 && (
                  <div className={`absolute z-50 mt-1 w-full rounded-xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {acNameItems.map((x, i) => (
                      <button key={i} type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setItemName(x); setAcNameOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm ${isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"}`}
                      >{x}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-center">
              <button onClick={handleSearch} className={primaryBtn} disabled={searchLoading}>
                <Search className="w-4 h-4" />
                {searchLoading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* ── Grid 1: Search Results ── */}
          {searchRows.length > 0 && (
            <div className={`${cardBase} mb-6`}>
              <div className={`px-5 py-3 border-b flex items-center justify-between ${isDark ? "border-white/10" : "border-gray-200"}`}>
                <span className={`text-sm font-bold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Search Results — {searchTotal} item(s) found
                </span>
                <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Showing {showingFrom}–{showingTo} of {searchTotal}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={primaryBtnClass}>
                    <tr>
                      <th className={thBase}>S.No</th>
                      <th className={thBase}>Item Code</th>
                      <th className={thBase}>Item Name</th>
                      <th className={thBase}>Part No</th>
                      <th className={thBase}>UOM</th>
                      <th className={thBase}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 cursor-pointer"
                          onChange={e => toggleAllGrid1(e.target.checked)}
                          title="Select All"
                        />
                        {" "}Select
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
                    {searchLoading
                      ? Array.from({ length: 5 }).map((_, i) => (
                          <tr key={i}>
                            {Array.from({ length: 6 }).map((__, c) => (
                              <td key={c} className="px-3 py-3">
                                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                              </td>
                            ))}
                          </tr>
                        ))
                      : pagedSearchRows.map((row, idx) => (
                          <tr key={row.itemid}
                            className={`${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"} transition`}>
                            <td className={tdBase}>{(searchPage - 1) * searchPageSize + idx + 1}</td>
                            <td className={tdBase}>{row.itemcode}</td>
                            <td className={`${tdBase} text-left`}>{row.itemname}</td>
                            <td className={tdBase}>{row.itemPartNo}</td>
                            <td className={tdBase}>{row.itemuom}</td>
                            <td className={tdBase}>
                              <input
                                type="checkbox"
                                className="w-4 h-4 cursor-pointer"
                                checked={!!grid1Checked[String(row.itemid)]}
                                onChange={e =>
                                  setGrid1Checked(prev => ({ ...prev, [String(row.itemid)]: e.target.checked }))
                                }
                              />
                            </td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {searchTotalPages > 1 && (
                <div className={`px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Page {searchPage} of {searchTotalPages}
                  </span>
                  <div className="inline-flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setSearchPage(p => Math.max(1, p - 1))}
                      disabled={searchPage === 1}
                      className={`px-3 py-1.5 text-sm ${searchPage === 1 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"}`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    {paginationButtons.map(pno => (
                      <button
                        key={pno}
                        onClick={() => setSearchPage(pno)}
                        className={`px-3 py-1.5 text-sm font-bold transition ${
                          searchPage === pno
                            ? `${primaryBtnClass} text-white`
                            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                        }`}
                      >{pno}</button>
                    ))}
                    <button
                      onClick={() => setSearchPage(p => Math.min(searchTotalPages, p + 1))}
                      disabled={searchPage === searchTotalPages}
                      className={`px-3 py-1.5 text-sm ${searchPage === searchTotalPages ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"}`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Add Button Footer */}
              <div className={`p-4 flex justify-center border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                <button onClick={handleAddItems} className={primaryBtn}>
                  <Plus className="w-4 h-4" /> Add Selected Items
                </button>
              </div>
            </div>
          )}

          {/* ── Grid 2: Cart / Added Items ── */}
          {cartRows.length > 0 && (
            <div className={`${cardBase}`}>
              <div className={`px-5 py-3 border-b flex items-center justify-between ${isDark ? "border-white/10" : "border-gray-200"}`}>
                <span className={`text-sm font-bold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Items to Issue — {cartRows.length} item(s)
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={primaryBtnClass}>
                    <tr>
                      <th className={thBase}>S.No</th>
                      <th className={thBase}>Item Code</th>
                      <th className={thBase}>Item Name</th>
                      <th className={thBase}>Part No</th>
                      <th className={thBase}>UOM</th>
                      <th className={thBase}>Qty</th>
                      <th className={thBase}>Tyre Serial No</th>
                      {/* Submit Select All */}
                      <th className={thBase}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 cursor-pointer mr-1"
                          onChange={e => toggleAllCart(e.target.checked)}
                        />
                        Select
                      </th>
                      {/* Remove Select All */}
                      <th className={thBase}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 cursor-pointer mr-1"
                          onChange={e => toggleAllRemove(e.target.checked)}
                        />
                        Remove
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
                    {cartRows.map((row, idx) => (
                      <tr key={row.itemid}
                        className={`${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"} transition`}>
                        <td className={tdBase}>{idx + 1}</td>
                        <td className={tdBase}>{row.itemcode}</td>
                        {/* Item Name as link (like ASP ButtonField CommandName="ViewHistory") */}
                        <td className={tdBase}>
                          <button
                            className="text-blue-500 hover:underline font-medium"
                            onClick={() => {/* ViewHistory handler if needed */}}
                          >{row.itemname}</button>
                        </td>
                        <td className={tdBase}>{row.itemPartNo}</td>
                        <td className={tdBase}>{row.itemuom}</td>
                        {/* Qty editable */}
                        <td className={tdBase}>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={getCartQty(row.itemid)}
                            onChange={e => setCartQty(row.itemid, e.target.value)}
                            className={`w-20 px-2 py-1.5 rounded-lg border text-center text-sm outline-none ${
                              isDark
                                ? "bg-gray-800 border-white/10 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          />
                        </td>
                        {/* Tyre Serial No link */}
                        <td className={tdBase}>
                          {row.fld_IsSerialNo ? (
                            <button
                              onClick={() => handleSerialNo(row)}
                              className="inline-flex items-center gap-1 text-blue-500 hover:underline text-sm font-medium"
                            >
                              <Link2 className="w-3 h-3" />
                              {row.serialno}
                            </button>
                          ) : (
                            <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                              {row.serialno}
                            </span>
                          )}
                        </td>
                        {/* Submit checkbox */}
                        <td className={tdBase}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 cursor-pointer"
                            checked={!!cartChecked[String(row.itemid)]}
                            onChange={e =>
                              setCartChecked(prev => ({ ...prev, [String(row.itemid)]: e.target.checked }))
                            }
                          />
                        </td>
                        {/* Remove checkbox */}
                        <td className={tdBase}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 cursor-pointer"
                            checked={!!cartRemoveChecked[String(row.itemid)]}
                            onChange={e =>
                              setCartRemoveChecked(prev => ({ ...prev, [String(row.itemid)]: e.target.checked }))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer: Submit + Remove */}
              <div className={`p-4 flex flex-wrap justify-center gap-4 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                <button onClick={handleSubmit} disabled={submitting} className={primaryBtn}>
                  <CheckSquare className="w-4 h-4" />
                  {submitting ? "Submitting..." : "Submit"}
                </button>
                <button onClick={handleRemoveItems} className={dangerBtn}>
                  <Trash2 className="w-4 h-4" /> Remove Selected
                </button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {searchRows.length === 0 && cartRows.length === 0 && (
            <div className={`${cardBase} p-12 flex flex-col items-center justify-center gap-3`}>
              <Search className={`w-12 h-12 ${isDark ? "text-gray-600" : "text-gray-300"}`} />
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Use the filter area above to search and add items
              </p>
            </div>
          )}

        </div>
      </div>
    </LayoutWrapper>
  );
};

export default SCRetrededTyreIssueEntry;